import { DataType } from "@/types"

export enum Operator {
	Equals,
	Contains,
	GreaterThan,
	LessThan,
	IsSet,
	NotSet,
	Any,
	Within,
	InTeam,
	NotEqual,
	NotAny,
	OnDay,
	DateRange
}

export interface OperatorLabel {
	operator: Operator,
	label: string
}

const operatorLabels: OperatorLabel[] = [
	{
		operator: Operator.Equals,
		label: "Equals"
	},
	{
		operator: Operator.Contains,
		label: "Contains"
	},
	{
		operator: Operator.GreaterThan,
		label: "Greater than"
	},
	{
		operator: Operator.LessThan,
		label: "Less than"
	},
	{
		operator: Operator.IsSet,
		label: "Is set"
	},
	{
		operator: Operator.NotSet,
		label: "Not set"
	},
	{
		operator: Operator.Within,
		label: "Within"
	},
	{
		operator: Operator.InTeam,
		label: "In Team"
	},
	{
		operator: Operator.NotEqual,
		label: "Not Equal"
	},
	{
		operator: Operator.OnDay,
		label: "On Day"
	},
	{
		operator: Operator.DateRange,
		label: "Date Range"
	}
]

const operatorsByFieldType: any = {
	text: [Operator.Equals, Operator.NotEqual, Operator.Contains, Operator.IsSet, Operator.NotSet],
	"text-area": [Operator.Equals, Operator.NotEqual, Operator.Contains, Operator.IsSet, Operator.NotSet],
	number: [Operator.Equals, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.IsSet, Operator.NotSet],
	dollar: [Operator.Equals, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.IsSet, Operator.NotSet],
	date: [Operator.Equals, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.IsSet, Operator.NotSet, Operator.DateRange],
	"date-time": [Operator.OnDay, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.IsSet, Operator.NotSet, Operator.DateRange],
	checkbox: [Operator.IsSet, Operator.NotSet],
	list: [Operator.Equals, Operator.NotEqual, Operator.IsSet, Operator.NotSet],
	"list-multiple": [Operator.Contains, Operator.IsSet, Operator.NotSet],
	"list-data": [Operator.Contains, Operator.IsSet, Operator.NotSet],
	"calculated": [Operator.Equals, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.IsSet, Operator.NotSet],
	"map-marker": [Operator.IsSet, Operator.NotSet, Operator.Within],
	"map-path": [Operator.IsSet, Operator.NotSet],
	"map-region": [Operator.IsSet, Operator.NotSet],
	"user-role": [Operator.Contains, Operator.IsSet, Operator.NotSet, Operator.Equals, Operator.NotEqual, Operator.GreaterThan, Operator.LessThan, Operator.InTeam],
	"time-spent": [Operator.Equals, Operator.GreaterThan, Operator.LessThan],
	"subrole": [Operator.Contains, Operator.IsSet, Operator.NotSet],
	"external-link": [Operator.Equals, Operator.NotEqual, Operator.Contains, Operator.IsSet, Operator.NotSet],
	"icon-upload": [Operator.Contains, Operator.IsSet, Operator.NotSet]
}

const isNotNull = (ruleValue: any, fieldValue: any) => fieldValue !== null && fieldValue !== undefined
const isNull = (ruleValue: any, fieldValue: any) => fieldValue === null || fieldValue === undefined

const startOfDay = (date: Date) => new Date(date.getTime() - date.getTime() % 86400000)
const addADay = (date: Date) => new Date(date.getTime() + (60 * 60 * 24 * 1000))

const getTimeFieldValue: (searchValueLabel: string, fieldValue: { totals: { fieldValue: string, hoursSpent: number }[] } | null) => number =
	(searchValueLabel: string, fieldValue: { totals: { fieldValue: string, hoursSpent: number }[] } | null) => {
		return fieldValue
			? fieldValue.totals.findAndGet(t => t.fieldValue === searchValueLabel, t => t.hoursSpent, 0)
			: 0
	}

type dataTypeOperations = {
	[key in Operator]?: (ruleValue: any, fieldValue: any) => boolean
}

export const defaultFieldOperator: { [key in DataType]?: Operator} = {
	[DataType.string]: Operator.Equals,
	[DataType.number]: Operator.Equals,
	[DataType.array]: Operator.Equals,
	[DataType.arrayMultiple]: Operator.Contains,
	[DataType.arrayData]: Operator.Contains,
	[DataType.boolean]: Operator.Equals,
	[DataType.mapPoint]: Operator.Within,
	[DataType.dateTime]: Operator.Equals,
	[DataType.calculated]: Operator.Equals,
	[DataType.userRole]: Operator.Contains
}

export const dataTypeTests: { [key in DataType]?: dataTypeOperations } = {
	[DataType.string]: {
		[Operator.IsSet]: isNotNull,
		[Operator.NotSet]: isNull,
		[Operator.Equals]: (ruleValue: string, fieldValue: string) => (!!fieldValue && fieldValue.toLowerCase() === ruleValue.toLowerCase()),
		[Operator.NotEqual]: (ruleValue: string, fieldValue: string) => !(!!fieldValue && fieldValue.toLowerCase() === ruleValue.toLowerCase()),
		[Operator.Contains]: (ruleValue: string, fieldValue: string) => !!fieldValue && fieldValue.toLowerCase().includes(ruleValue.toLowerCase())
	},
	[DataType.number]: {
		[Operator.IsSet]: isNotNull,
		[Operator.NotSet]: isNull,
		// eslint-disable-next-line eqeqeq
		[Operator.Equals]: (ruleValue: number, fieldValue: number) => fieldValue == ruleValue,
		[Operator.NotEqual]: (ruleValue: number, fieldValue: number) => fieldValue !== ruleValue,
		[Operator.GreaterThan]: (ruleValue: number, fieldValue: number) => fieldValue > ruleValue,
		[Operator.LessThan]: (ruleValue: number, fieldValue: number) => fieldValue < ruleValue
	},
	[DataType.dateTime]: {
		[Operator.IsSet]: isNotNull,
		[Operator.NotSet]: isNull,
		// eslint-disable-next-line eqeqeq
		[Operator.Equals]: (ruleValue: any, fieldValue: any) => {
			const range = ruleValue.start && ruleValue.end
				? ruleValue
				: {
					start: new Date(ruleValue),
					end: addADay(new Date(ruleValue))
				}

			const fieldStart = new Date(fieldValue)
			return fieldValue && fieldStart >= range.start && fieldStart < range.end
		},
		// eslint-disable-next-line eqeqeq
		[Operator.NotEqual]: (ruleValue: any, fieldValue: any) => {
			const range = ruleValue.start && ruleValue.end
				? ruleValue
				: {
					start: new Date(ruleValue),
					end: addADay(new Date(ruleValue))
				}

			const fieldStart = new Date(fieldValue)
			return !(fieldValue && fieldStart >= range.start && fieldStart < range.end)
		},
		// eslint-disable-next-line eqeqeq
		[Operator.OnDay]: (ruleValue: any, fieldValue: any) => {
			const range = ruleValue.start && ruleValue.end
				? ruleValue
				: {
					start: new Date(ruleValue),
					end: addADay(new Date(ruleValue))
				}

			const fieldStart = new Date(fieldValue)
			return fieldValue && fieldStart >= range.start && fieldStart < range.end
		},
		[Operator.GreaterThan]: (ruleValue: any, fieldValue: any) => fieldValue && new Date(fieldValue) > new Date(ruleValue),
		[Operator.LessThan]: (ruleValue: any, fieldValue: any) => fieldValue && new Date(fieldValue) < new Date(ruleValue),
		[Operator.DateRange]: (ruleValue: any, fieldValue: any) => fieldValue && new Date(fieldValue) > ruleValue.start && new Date(fieldValue) < ruleValue.end
	},
	[DataType.boolean]: {
		[Operator.IsSet]: (ruleValue: boolean, fieldValue: boolean) => !!fieldValue,
		[Operator.NotSet]: (ruleValue: boolean, fieldValue: boolean) => !fieldValue
	},
	[DataType.array]: {
		[Operator.IsSet]: isNotNull,
		[Operator.NotSet]: isNull,
		[Operator.Equals]: (ruleValue: any[], fieldValue: any[]) => fieldValue === ruleValue,
		[Operator.NotEqual]: (ruleValue: any[], fieldValue: any[]) => fieldValue !== ruleValue
	},
	[DataType.arrayMultiple]: {
		[Operator.IsSet]: (ruleValue: any, fieldValue: any) => fieldValue !== null && fieldValue !== undefined && fieldValue.length > 0,
		[Operator.NotSet]: (ruleValue: any, fieldValue: any) => fieldValue === null || fieldValue === undefined || fieldValue.length === 0,
		[Operator.Contains]: (ruleValue: any, fieldValue: any) => fieldValue && fieldValue.some((v: any) => v.id === ruleValue)
	},
	[DataType.userRole]: {
		[Operator.IsSet]: (ruleValue: any, fieldValue: any) => fieldValue && ((fieldValue.users && fieldValue.users.length > 0) || (fieldValue.subRoles && fieldValue.subRoles.some((r: any) => r.users.length > 0))),
		[Operator.NotSet]: (ruleValue: any, fieldValue: any) => !fieldValue || !((fieldValue.users && fieldValue.users.length > 0) || (fieldValue.subRoles && fieldValue.subRoles.some((r: any) => r.users.length > 0))),
		[Operator.Contains]: (ruleValue: any, fieldValue: any) => {
			if (!fieldValue) {
				return false
			}
			const roleUsers = [
				...(fieldValue.users ? fieldValue.users : []),
				...(fieldValue.subRoles ? fieldValue.subRoles.map((sr: any) => sr.users).filter((u: any) => u !== null).flat() : [])
			]
			return !!roleUsers.find(u => u.id === ruleValue)
		},
		// eslint-disable-next-line eqeqeq
		[Operator.Equals]: (ruleValue: any, fieldValue: any) => (!fieldValue || fieldValue.totalTime === undefined || fieldValue.totalTime === null) ? ruleValue === 0 : fieldValue.totalTime == ruleValue,
		// eslint-disable-next-line eqeqeq
		[Operator.NotEqual]: (ruleValue: any, fieldValue: any) => (!fieldValue || fieldValue.totalTime === undefined || fieldValue.totalTime === null) ? ruleValue != 0 : fieldValue.totalTime != ruleValue,
		[Operator.GreaterThan]: (ruleValue: any, fieldValue: any) => (!fieldValue || fieldValue.totalTime === undefined || fieldValue.totalTime === null) ? ruleValue < 0 : fieldValue.totalTime > ruleValue,
		[Operator.LessThan]: (ruleValue: any, fieldValue: any) => (!fieldValue || fieldValue.totalTime === undefined || fieldValue.totalTime === null) ? ruleValue > 0 : fieldValue.totalTime < ruleValue
	},
	[DataType.timeSpentAsValue]: {
		[Operator.IsSet]: (ruleValue: any, fieldValue: any) => !!fieldValue,
		[Operator.NotSet]: (ruleValue: any, fieldValue: any) => !fieldValue,
		[Operator.Equals]: (ruleValue: any, fieldValue: any) => {
			if (ruleValue.hours !== undefined && ruleValue.searchValueLabel !== undefined) {
				return getTimeFieldValue(ruleValue.searchValueLabel, fieldValue) === ruleValue.hours
			}
			return true
		},
		[Operator.NotEqual]: (ruleValue: any, fieldValue: any) => {
			if (ruleValue.hours !== undefined && ruleValue.searchValueLabel !== undefined) {
				return getTimeFieldValue(ruleValue.searchValueLabel, fieldValue) !== ruleValue.hours
			}
			return true
		},
		[Operator.GreaterThan]: (ruleValue: any, fieldValue: any) => {
			if (ruleValue.hours !== undefined && ruleValue.searchValueLabel !== undefined) {
				return getTimeFieldValue(ruleValue.searchValueLabel, fieldValue) > ruleValue.hours
			}
			return true
		},
		[Operator.LessThan]: (ruleValue: any, fieldValue: any) => {
			if (ruleValue.hours !== undefined && ruleValue.searchValueLabel !== undefined) {
				return getTimeFieldValue(ruleValue.searchValueLabel, fieldValue) < ruleValue.hours
			}
			return true
		}
	}
}

export { operatorLabels, operatorsByFieldType }
